/**
 * useAISearchEvent Hook
 *
 * Custom hook for tracking AI Search SEARCH events in the chat renderer.
 * Implements tracking requirements from Conversational AI Search Tracking Requirements.
 *
 * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/2345959449
 */

import { useCallback } from 'react';
import { useSession } from '@marriott/mi-headless-utils';
import { searchACDLkeyGenerator, SearchResultProperty, AI_SEARCH_TYPE, ACDL_EVENT_NAMES } from '../utils/acdlUtils';

type DataLayerEventType = {
  event: string;
  skipDatalayerUpdate?: boolean;
  [key: string]: { [key: string]: unknown } | string | undefined | boolean;
};

export interface UseAISearchEventOptions {
  /** Number of properties returned in search results */
  numberOfResults: number;
  /** Array of properties returned in search results */
  properties?: SearchResultProperty[];
}

export interface UseAISearchEventReturn {
  /** Manually trigger a SEARCH event */
  trackSearchEvent: (options: UseAISearchEventOptions) => void;
  /** Whether ACDL is enabled */
  isACDLEnabled: boolean;
  /** Whether the datalayer is ready */
  isDataLayerReady: boolean;
}

/**
 * Hook for tracking AI Search SEARCH events
 *
 * The SEARCH event should be triggered when search results are displayed back to the user.
 * The event should be triggered with each search regardless of whether results are displayed
 * or if there are no results.
 *
 * @example
 * // Track search results when they are returned
 * const { trackSearchEvent, isDataLayerReady } = useAISearchEvent();
 *
 * useEffect(() => {
 *   if (searchResults && isDataLayerReady) {
 *     trackSearchEvent({
 *       numberOfResults: searchResults.length,
 *       properties: searchResults.map(p => ({
 *         propertyId: p.id,
 *         cost: p.averageNightlyRate,
 *       })),
 *     });
 *   }
 * }, [searchResults, isDataLayerReady, trackSearchEvent]);
 *
 * @example
 * // Track no results scenario
 * const { trackSearchEvent } = useAISearchEvent();
 *
 * if (searchResults.length === 0) {
 *   trackSearchEvent({
 *     numberOfResults: 0,
 *     properties: [],
 *   });
 * }
 */
export function useAISearchEvent(): UseAISearchEventReturn {
  const { isACDLenabled, datalayer, updateDatalayer } = useSession();
  const isDataLayerReady = Boolean(isACDLenabled && datalayer);

  /**
   * Track a SEARCH event
   */
  const trackSearchEvent = useCallback(
    (options: UseAISearchEventOptions) => {
      if (!isACDLenabled || !datalayer) {
        console.debug('[useAISearchEvent] ACDL not enabled or datalayer not ready');
        return;
      }

      const { numberOfResults, properties = [] } = options;

      const event = searchACDLkeyGenerator({
        datalayer,
        numberOfResults,
        properties,
      });

      console.debug('[useAISearchEvent] Tracking SEARCH:', event);

      updateDatalayer(event as DataLayerEventType);
    },
    [isACDLenabled, datalayer, updateDatalayer]
  );

  return {
    trackSearchEvent,
    isACDLEnabled: isACDLenabled,
    isDataLayerReady,
  };
}

export type { SearchResultProperty };
export { AI_SEARCH_TYPE, ACDL_EVENT_NAMES };
